import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

// Create a ticket block — a transport group of journeyType TICKET. It has no
// route (no stops/times); the camp manager fills it with an event group's people
// and later confirms tickets are secured. It attaches to the current campus via
// the journey's `campus` field (the belongsToCampus fallback), so no stop needed.
export default function AddTicketBlockDialog({ open, onClose, campDate, campusName, onCreate }) {
  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState(50);
  const [supplier, setSupplier] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setCapacity(50);
    setSupplier("");
    setSaving(false);
  }, [open]);

  const cap = Number(capacity);
  const canSave =
    String(name).trim() !== "" && Number.isFinite(cap) && cap > 0 && Boolean(campusName);

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onCreate?.({
        campDate,
        groupName: String(name).trim(),
        capacity: cap,
        campus: campusName, // belongsToCampus fallback keys on the campus name
        status: "Planned",
        meta: {
          journeyType: "TICKET",
          provider: String(supplier).trim() || null,
          contactName: null,
          contactNumber: null,
          clearFields: [],
        },
        stops: [],
      });
      onClose?.();
    } catch {
      // hook surfaces its own error toast; keep the dialog open to retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add ticket block</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            A ticket block holds everyone who needs a ticket for an event. Add the event
            group's members to it, then confirm the tickets are secured.
          </Typography>

          <TextField
            label="Name"
            placeholder="e.g. Castle trip tickets"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
          />
          <TextField
            label="Capacity (max tickets)"
            type="number"
            value={capacity}
            onChange={(e) => setCapacity(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <TextField
            label="Supplier (optional)"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            fullWidth
          />

          {!campusName && (
            <Typography variant="caption" color="error">
              Pick a campus first.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? "Saving…" : "Add ticket block"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
